import { AfterViewInit, Component, DestroyRef, inject, TemplateRef, viewChild } from '@angular/core';
import { UserLevel } from '@app/core/models/user-level';
import {
  CommandDefinition,
  CommandHelper,
  CommandResultEntry,
  QuickCommandService,
} from '@app/shared/quick-commands';
import { BootstrapSemanticVariation } from '@app/utils/bootstrap';
import { updateRxCachedLoggers } from '@app/utils/cached';
import { Logger } from '@app/utils/logger';
import { sortedBy } from '@app/utils/sort/sort-by';
import { TranslatePipe } from '@ngx-translate/core';
import log, { LogLevelNumbers } from 'loglevel';
import { environment } from '../../../../environments/environment';

const Levels: Record<LogLevelNumbers, string> = {
  0: 'trace',
  1: 'debug',
  2: 'info',
  3: 'warn',
  4: 'error',
  5: 'silent',
};

@Component({
  selector: 'portal-log-level-command',
  templateUrl: './log-level.command.component.html',
  imports: [TranslatePipe],
  host: {
    class: 'd-flex align-items-center flex-grow-1',
  },
})
export class LogLevelCommandComponent implements AfterViewInit {
  protected entryTemplate = viewChild<TemplateRef<any>>('item');
  protected ColorMapping: Record<string, BootstrapSemanticVariation> = {
    debug: 'success',
    error: 'danger',
    info: 'info',
    warn: 'warning',
    trace: 'dark',
    silent: 'light',
  };

  private destroyRef = inject(DestroyRef);
  private quickCommandService = inject(QuickCommandService);

  ngAfterViewInit(): void {
    const dispose = this.quickCommandService.add(this.getCommand());
    this.destroyRef.onDestroy(dispose);
  }

  getCommand(): CommandDefinition<['all' | string, log.LogLevelDesc]> {
    return {
      authorization: {
        requiredUserLevel: environment.production ? UserLevel.SystemAdmin : UserLevel.All,
      },
      icon: 'far fa-debug',
      label: 'quickCommands.commands.logLevel',
      name: 'logLevel',
      section: QuickCommandService.DevSection,
      template: this.entryTemplate(),
      action(_, [name, newLevel]) {
        if (name != null && newLevel != null) {
          if (name === 'all') {
            Logger.setDefaultLevel(newLevel);

            Object.values(Logger.getLoggers()).forEach((logger) => {
              logger.setLevel(newLevel);
            });
          } else {
            Logger.getLogger(name).setLevel(newLevel);
          }

          updateRxCachedLoggers();
          return CommandHelper.return({
            refresh: true,
          });
        } else if (name != null) {
          let level: LogLevelNumbers;

          if (name === 'all') {
            level = Logger.getLevel();
          } else {
            const logger = Logger.getLogger(name);
            level = logger.getLevel();
          }

          return CommandHelper.list({
            filterAutomatically: true,
            values: [
              {
                active: level === 0,
                label: 'common.logLevel.TRACE',
                value: 0,
              },
              {
                active: level === 1,
                label: 'common.logLevel.DEBUG',
                value: 1,
              },
              {
                active: level === 2,
                label: 'common.logLevel.INFO',
                value: 2,
              },
              {
                active: level === 3,
                label: 'common.logLevel.WARN',
                value: 3,
              },
              {
                active: level === 4,
                label: 'common.logLevel.ERROR',
                value: 4,
              },
              {
                active: level === 5,
                label: 'common.logLevel.SILENT',
                value: 5,
              },
            ],
          });
        }

        const allLoggers: CommandResultEntry<'all'> = {
          label: 'quickCommands.commands.logLevel.all',
          value: 'all',
        };

        const namedLoggers = Object.entries(Logger.getLoggers())
          .map(([name, logger]): CommandResultEntry<string> => {
            const level = Levels[logger.getLevel()];

            return {
              group: 'quickCommands.commands.logLevel.namedLoggers',
              label: `${name}: ${level}`,
              meta: {
                level,
                name,
              },
              value: name,
            };
          })
          .toSorted(
            sortedBy({
              ignoreCase: true,
              key: 'label',
              order: 'asc',
            })
          );

        return CommandHelper.list({
          filterAutomatically: true,
          values: [allLoggers, ...namedLoggers],
        });
      },
    };
  }
}
